import { ReactNode, useCallback, useEffect, useRef } from "react";
import { OptionData } from "@/components/form-control";
import { useVirtualizer } from "@tanstack/react-virtual";

type ScrollInfiniteProps<T> = {
  onLoadMore: () => Promise<void> | void;
  children?: ReactNode;
  hasFixedHeight?: boolean;
  threshold?: number;
  className?: string;
  hasNextPage?: boolean;
  loadingIndicator?: ReactNode;
  endMessage?: ReactNode;
  allRows: T[];
  isLoading?: boolean;
};

export const ScrollInfinite = <T extends OptionData<string>>({
  onLoadMore,
  children,
  hasNextPage = true,
  loadingIndicator,
  className,
  allRows,
  isLoading,
  threshold = 50,
  hasFixedHeight = true,
  endMessage,
}: ScrollInfiniteProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 6,
    overscan: 6,
  });

  // Use refs to hold mutable flags so they persist across renders/effect re-creations
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);

  // Wrap onLoadMore so we consistently return a Promise (and make it stable if caller doesn't)
  const callLoadMore = useCallback(async () => {
    // Prevent concurrent calls at this call-site level too (defensive)
    if (pendingRef.current) return;
    pendingRef.current = true;
    try {
      await onLoadMore();
    } finally {
      pendingRef.current = false;
    }
  }, [onLoadMore]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    mountedRef.current = true;

    const handleScroll = async () => {
      if (!mountedRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom <= threshold && hasNextPage && !isLoading && !pendingRef.current) {
        // callLoadMore already checks/set pendingRef, but keep here as fast guard to avoid scheduling unnecessary work
        await callLoadMore();
      }
    };

    el.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      mountedRef.current = false;
      el.removeEventListener("scroll", handleScroll);
    };
  }, [callLoadMore, hasNextPage, isLoading, threshold]);

  return (
    <div
      ref={scrollContainerRef}
      className={"List " + (className ? ` ${className}` : "")}
      style={
        hasFixedHeight
          ? {
              height: "190px",
              width: "100%",
              overflow: "auto",
            }
          : {
              width: "100%",
              overflow: "auto",
            }
      }
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {children}
        {isLoading && (
          <div className="absolute left-0 right-0 text-center py-2">{loadingIndicator || <div>Loading...</div>}</div>
        )}
        {!hasNextPage && endMessage && <div className="absolute left-0 right-0 text-center py-2">{endMessage}</div>}
      </div>
    </div>
  );
};
